<template>
  <div class="permission-type-manager">
    <div class="header">
      <h2>Permission Types</h2>
      <p class="description">
        Manage permission types that can be assigned to roles. Permission types define the actions users can perform in the system.
      </p>
    </div>

    <!-- Filter/Search Section -->
    <div class="filter-section">
      <div class="form-group">
        <label for="categoryFilter">Filter by Category:</label>
        <select id="categoryFilter" v-model="selectedCategory">
          <option value="">All Categories</option>
          <option v-for="cat in categories" :key="cat" :value="cat">{{ cat }}</option>
        </select>
      </div>
      <div class="form-group">
        <label for="searchKey">Search by Key:</label>
        <input
          id="searchKey"
          v-model="searchKey"
          type="text"
          placeholder="Search permission key..."
        />
      </div>
    </div>

    <!-- Permission Types List -->
    <div class="permissions-section">
      <div class="section-header">
        <h3>Permission Types ({{ filteredPermissionTypes.length }} of {{ permissionTypes.length }})</h3>
        <div class="actions">
          <button @click="refreshPermissionTypes" :disabled="isLoading" class="btn btn-sm btn-warning mr-2">
            <i class="fas fa-sync-alt"></i> {{ isLoading ? 'Loading...' : 'Refresh' }}
          </button>
          <button @click="showAddPermissionTypeModal" class="btn btn-success">
            <i class="fas fa-plus"></i> Add Permission Type
          </button>
        </div>
      </div>

      <div v-if="isLoading" class="loading">
        <p>Loading permission types...</p>
      </div>

      <div v-else-if="permissionTypes.length === 0" class="empty-state">
        <p>No permission types found. Add your first permission type above.</p>
      </div>

      <div v-else class="permissions-by-category">
        <div class="table-responsive">
          <table class="permission-table">
            <thead>
              <tr>
                <th style="width: 15%;">Category</th>
                <th style="width: 20%;">Permission Key</th>
                <th style="width: 25%;">Display Name</th>
                <th style="width: 25%;">Description</th>
                <th style="width: 8%;">Status</th>
                <th style="width: 7%;">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in flatPermissionList" :key="row.id" :class="{ 'category-row': row.isCategory }">
                <template v-if="row.isCategory">
                  <td colspan="6" class="category-cell">
                    <span class="category-label">{{ row.category }}</span>
                  </td>
                </template>
                <template v-else>
                  <td></td>
                  <td><code>{{ row.key }}</code></td>
                  <td>{{ row.name }}</td>
                  <td>{{ row.description || 'No description' }}</td>
                  <td>
                    <span :class="['badge', row.is_active ? 'badge-success' : 'badge-inactive']">
                      {{ row.is_active ? 'Active' : 'Inactive' }}
                    </span>
                  </td>
                  <td class="actions">
                    <button @click="showEditPermissionTypeModal(row)" class="btn btn-sm btn-primary mr-1">
                      <i class="fas fa-edit"></i>
                    </button>
                    <button @click="confirmDelete(row)" class="btn btn-sm btn-danger">
                      <i class="fas fa-trash"></i>
                    </button>
                  </td>
                </template>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Add/Edit Permission Type Modal -->
    <div class="modal fade" id="permissionTypeModal" tabindex="-1" role="dialog">
      <div class="modal-dialog" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ editingPermissionType ? 'Edit Permission Type' : 'Add New Permission Type' }}</h5>
            <button type="button" class="close" data-dismiss="modal">
              <span>&times;</span>
            </button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="savePermissionType">
              <div class="form-group">
                <label for="key">Permission Key *</label>
                <input
                  id="key"
                  v-model="formData.key"
                  type="text"
                  class="form-control"
                  placeholder="e.g., retention.view"
                  :disabled="editingPermissionType !== null"
                  @blur="validateKey"
                  required
                />
                <small class="form-text text-muted">Format: category.action (lowercase, alphanumeric, dots only)</small>
                <span v-if="keyError" class="text-danger">{{ keyError }}</span>
              </div>
              
              <div class="form-group">
                <label for="category">Category *</label>
                <input
                  id="category"
                  v-model="formData.category"
                  type="text"
                  class="form-control"
                  placeholder="e.g., Retention"
                  required
                />
                <span v-if="categoryError" class="text-danger">{{ categoryError }}</span>
              </div>
              
              <div class="form-group">
                <label for="name">Display Name *</label>
                <input
                  id="name"
                  v-model="formData.name"
                  type="text"
                  class="form-control"
                  placeholder="e.g., View Retention Messages"
                  required
                />
                <span v-if="nameError" class="text-danger">{{ nameError }}</span>
              </div>
              
              <div class="form-group">
                <label for="description">Description</label>
                <textarea
                  id="description"
                  v-model="formData.description"
                  class="form-control"
                  rows="3"
                  placeholder="Describe what this permission allows users to do"
                />
              </div>
              
              <div class="form-group">
                <div class="form-check">
                  <input
                    type="checkbox"
                    v-model="formData.is_active"
                    class="form-check-input"
                    id="isActive"
                  />
                  <label class="form-check-label" for="isActive">
                    Active
                  </label>
                </div>
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-dismiss="modal">Cancel</button>
            <button @click="savePermissionType" class="btn btn-primary" :disabled="!isFormValid || isSaving">
              {{ isSaving ? (editingPermissionType ? 'Updating...' : 'Adding...') : 'Save' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div v-if="deleteConfirmation" class="modal-overlay" @click="deleteConfirmation = null">
      <div class="modal-content" @click.stop>
        <h3>Confirm Deletion</h3>
        <p>Are you sure you want to delete the permission type:</p>
        <p class="delete-item"><strong>{{ deleteConfirmation.key }}</strong> - {{ deleteConfirmation.name }}</p>
        <p class="warning">This action cannot be undone. Roles using this permission will lose access.</p>
        <div class="modal-actions">
          <button @click="deletePermissionType" :disabled="isDeleting" class="btn btn-danger">
            <span v-if="isDeleting">Deleting...</span>
            <span v-else">Confirm Delete</span>
          </button>
          <button @click="deleteConfirmation = null" class="btn btn-secondary">Cancel</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { permissionTypeService } from '../../services/permissionType.service';
import notify from '../../util/notification';

export default {
  name: 'PermissionTypeManager',
  data() {
    return {
      permissionTypes: [],
      isLoading: false,
      isSaving: false,
      isDeleting: false,
      formData: {
        key: '',
        name: '',
        category: '',
        description: '',
        is_active: true
      },
      keyError: '',
      nameError: '',
      categoryError: '',
      editingPermissionType: null,
      deleteConfirmation: null,
      selectedCategory: '',
      searchKey: ''
    };
  },
  computed: {
    isFormValid() {
      return this.formData.key && this.formData.name && this.formData.category && 
             !this.keyError && !this.nameError && !this.categoryError;
    },
    categories() {
      const cats = new Set(this.permissionTypes.map(p => p.category));
      return Array.from(cats).sort();
    },
    filteredPermissionTypes() {
      return this.permissionTypes.filter(p => {
        const matchesCategory = !this.selectedCategory || p.category === this.selectedCategory;
        const matchesSearch = !this.searchKey || p.key.toLowerCase().includes(this.searchKey.toLowerCase());
        return matchesCategory && matchesSearch;
      });
    },
    groupedPermissions() {
      const grouped = {};
      this.filteredPermissionTypes.forEach(p => {
        if (!grouped[p.category]) {
          grouped[p.category] = [];
        }
        grouped[p.category].push(p);
      });
      
      // Sort permissions within each category
      Object.keys(grouped).forEach(cat => {
        grouped[cat].sort((a, b) => a.key.localeCompare(b.key));
      });
      
      return grouped;
    },
    flatPermissionList() {
      const list = [];
      const grouped = this.groupedPermissions;
      
      Object.keys(grouped).sort().forEach(category => {
        // Add category header row
        list.push({
          isCategory: true,
          category: category,
          id: `category-${category}`
        });
        
        // Add permission rows
        grouped[category].forEach(permission => {
          list.push({
            isCategory: false,
            ...permission
          });
        });
      });
      
      return list;
    }
  },
  async mounted() {
    await this.loadPermissionTypes();
  },
  methods: {
    async loadPermissionTypes() {
      this.isLoading = true;
      try {
        await this.$store.dispatch('fetchPermissionTypes');
        this.permissionTypes = this.$store.getters.permissionTypes;
      } catch (error) {
        notify('Error loading permission types', 'error');
        console.error('Error loading permission types:', error);
      } finally {
        this.isLoading = false;
      }
    },
    async refreshPermissionTypes() {
      await this.loadPermissionTypes();
      notify('Permission types refreshed', 'success');
    },
    showAddPermissionTypeModal() {
      this.editingPermissionType = null;
      this.resetForm();
      $('#permissionTypeModal').modal('show');
    },
    showEditPermissionTypeModal(permission) {
      this.editingPermissionType = permission;
      this.formData = {
        key: permission.key,
        name: permission.name,
        category: permission.category,
        description: permission.description || '',
        is_active: permission.is_active !== false
      };
      this.keyError = '';
      this.nameError = '';
      this.categoryError = '';
      $('#permissionTypeModal').modal('show');
    },
    validateKey() {
      this.keyError = '';
      if (!this.formData.key) {
        this.keyError = 'Permission key is required';
        return;
      }
      
      const keyPattern = /^[a-z0-9_]+\.[a-z0-9_]+$/;
      if (!keyPattern.test(this.formData.key)) {
        this.keyError = 'Invalid format. Use: category.action (lowercase, alphanumeric)';
        return;
      }
      
      // Check for duplicates (only if not editing or if key changed)
      if (!this.editingPermissionType) {
        const exists = this.permissionTypes.some(p => p.key === this.formData.key);
        if (exists) {
          this.keyError = 'Permission key already exists';
        }
      }
    },
    async savePermissionType() {
      this.validateKey();
      if (!this.isFormValid) return;
      
      this.isSaving = true;
      try {
        if (this.editingPermissionType) {
          await permissionTypeService.editPermissionType(this.editingPermissionType.id, this.formData);
          notify('Permission type updated successfully', 'success');
        } else {
          await permissionTypeService.addPermissionType(this.formData);
          notify('Permission type added successfully', 'success');
        }
        
        $('#permissionTypeModal').modal('hide');
        await this.loadPermissionTypes();
        this.resetForm();
      } catch (error) {
        const message = error.response?.data?.message || 'Error saving permission type';
        notify(message, 'error');
        console.error('Error saving permission type:', error);
      } finally {
        this.isSaving = false;
      }
    },
    editPermissionType(permission) {
      // Deprecated - use showEditPermissionTypeModal instead
      this.showEditPermissionTypeModal(permission);
    },
    confirmDelete(permission) {
      this.deleteConfirmation = permission;
    },
    async deletePermissionType() {
      if (!this.deleteConfirmation) return;
      
      this.isDeleting = true;
      try {
        await permissionTypeService.deletePermissionType(this.deleteConfirmation.id);
        notify('Permission type deleted successfully', 'success');
        await this.loadPermissionTypes();
        this.deleteConfirmation = null;
      } catch (error) {
        const message = error.response?.data?.message || 'Error deleting permission type';
        notify(message, 'error');
        console.error('Error deleting permission type:', error);
      } finally {
        this.isDeleting = false;
      }
    },
    resetForm() {
      this.formData = {
        key: '',
        name: '',
        category: '',
        description: '',
        is_active: true
      };
      this.keyError = '';
      this.nameError = '';
      this.categoryError = '';
      this.editingPermissionType = null;
    }
  }
};
</script>

<style scoped>
.permission-type-manager {
  padding: 0;
}

.header {
  margin-bottom: 20px;
}

.header h2 {
  margin: 0 0 10px 0;
  color: #ffffff;
  font-size: 1.5rem;
}

.description {
  color: #adb5bd;
  margin: 0;
  font-size: 0.9rem;
}

.filter-section {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
  margin-bottom: 20px;
  padding: 15px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  border: 1px solid #444;
}

.filter-section .form-group {
  margin-bottom: 0;
}

.filter-section label {
  display: block;
  margin-bottom: 5px;
  font-weight: 500;
  color: #ffffff;
  font-size: 0.875rem;
}

.filter-section input,
.filter-section select {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #555;
  border-radius: 4px;
  font-size: 14px;
  background-color: #3a3a3a;
  color: #ffffff;
}

.filter-section input:focus,
.filter-section select:focus {
  background-color: #3a3a3a;
  border-color: #007bff;
  outline: none;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.permissions-section {
  margin-top: 20px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.section-header h3 {
  margin: 0;
  color: #ffffff;
  font-size: 1.25rem;
}

.section-header .actions {
  display: flex;
  gap: 10px;
}

.loading, .empty-state {
  text-align: center;
  padding: 40px;
  color: #adb5bd;
  background-color: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  border: 1px solid #444;
}

.category-group {
  margin-bottom: 30px;
}

.table-responsive {
  width: 100%;
  overflow-x: auto;
}

.category-row {
  background-color: rgba(0, 123, 255, 0.08) !important;
}

.category-row:hover {
  background-color: rgba(0, 123, 255, 0.08) !important;
}

.category-cell {
  padding: 12px 15px !important;
  border-bottom: 2px solid #007bff !important;
}

.category-label {
  color: #4ea1ff;
  font-size: 15px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.category-header {
  background: rgba(0, 123, 255, 0.1);
  padding: 10px 15px;
  margin: 0 0 10px 0;
  border-radius: 4px;
  color: #4ea1ff;
  font-size: 16px;
  font-weight: 600;
  border-left: 3px solid #007bff;
}

.permission-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 20px;
  background: #2b2b2b;
  box-shadow: 0 1px 3px rgba(0,0,0,0.3);
  border-radius: 8px;
  overflow: hidden;
  table-layout: fixed;
}

.permission-table th,
.permission-table td {
  padding: 12px 15px;
  text-align: left;
  border-bottom: 1px solid #444;
  overflow: hidden;
  text-overflow: ellipsis;
}

.permission-table th {
  background: #3a3a3a;
  font-weight: 600;
  color: #ffffff;
  text-transform: uppercase;
  font-size: 0.75rem;
  letter-spacing: 0.5px;
}

.permission-table tbody tr {
  background-color: #2b2b2b;
  transition: background-color 0.2s;
}

.permission-table tbody tr:hover {
  background-color: rgba(255, 255, 255, 0.05);
}

.permission-table td {
  color: #e9ecef;
}

.permission-table code {
  background: rgba(0, 123, 255, 0.15);
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 13px;
  color: #4ea1ff;
  font-weight: 500;
}

.badge {
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.badge-success {
  background: rgba(40, 167, 69, 0.2);
  color: #28a745;
  border: 1px solid rgba(40, 167, 69, 0.3);
}

.badge-inactive {
  background: rgba(220, 53, 69, 0.2);
  color: #dc3545;
  border: 1px solid rgba(220, 53, 69, 0.3);
}

/* Modal Styling */
.modal-content {
  background-color: #2b2b2b;
  color: #ffffff;
  border: 1px solid #444;
}

.modal-header {
  border-bottom: 1px solid #444;
  padding: 20px 25px;
}

.modal-header .modal-title {
  color: #ffffff;
  font-weight: 600;
  font-size: 1.25rem;
}

.modal-header .close {
  color: #ffffff;
  opacity: 0.8;
  font-size: 1.5rem;
  padding: 0;
  margin: -5px -5px 0 0;
}

.modal-header .close:hover {
  opacity: 1;
}

.modal-footer {
  border-top: 1px solid #444;
  padding: 15px 25px;
}

.modal-body {
  color: #e9ecef;
  padding: 20px 25px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group:last-of-type {
  margin-bottom: 15px;
}

.form-control {
  background-color: #3a3a3a;
  border: 1px solid #555;
  color: #ffffff;
  padding: 10px 12px;
  font-size: 14px;
}

.form-control:focus {
  background-color: #3a3a3a;
  border-color: #007bff;
  color: #ffffff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.form-control::placeholder {
  color: #6c757d;
}

.form-group label {
  color: #ffffff;
  font-weight: 500;
  margin-bottom: 8px;
  display: block;
}

.form-text {
  color: #adb5bd;
  font-size: 0.875rem;
  margin-top: 6px;
  display: block;
}

.text-danger {
  color: #dc3545;
  font-size: 12px;
  margin-top: 5px;
  display: block;
}

/* Checkbox styling */
.form-check {
  display: flex;
  align-items: center;
  margin: 0;
  padding: 0;
}

/* Checkbox in modal form - cleaner look */
.modal-body .form-check {
  padding: 8px 0;
  background: transparent;
  border: none;
  border-radius: 0;
}

.form-check-input[type="checkbox"] {
  display: inline-block !important;
  visibility: visible !important;
  opacity: 1 !important;
  position: relative !important;
  width: 18px !important;
  height: 18px !important;
  margin: 0 !important;
  margin-right: 10px !important;
  background-color: #2b3553 !important;
  border: 1px solid #4a5568 !important;
  border-radius: 4px !important;
  cursor: pointer !important;
  flex-shrink: 0;
}

.form-check-input[type="checkbox"]:checked {
  background-color: #1d8cf8 !important;
  border-color: #1d8cf8 !important;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'%3e%3cpath fill='none' stroke='%23fff' stroke-linecap='round' stroke-linejoin='round' stroke-width='3' d='m6 10 3 3 6-6'/%3e%3c/svg%3e") !important;
  background-size: 14px !important;
  background-position: center !important;
  background-repeat: no-repeat !important;
}

.form-check-label {
  padding-left: 0;
  color: #ffffff;
  margin: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  user-select: none;
  font-size: 14px;
  margin-bottom: 0 !important; 
}

/* Delete modal styling */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-overlay .modal-content {
  background: #2b2b2b;
  padding: 30px;
  border-radius: 8px;
  max-width: 500px;
  width: 90%;
  color: #ffffff;
  border: 1px solid #444;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
}

.modal-overlay .modal-content h3 {
  margin-top: 0;
  color: #ffffff;
}

.modal-overlay .modal-content p {
  color: #e9ecef;
}

.delete-item {
  background: #3a3a3a;
  padding: 12px;
  border-radius: 4px;
  margin: 15px 0;
  border-left: 3px solid #007bff;
}

.delete-item strong {
  color: #4ea1ff;
}

.warning {
  color: #dc3545;
  font-size: 14px;
  margin: 15px 0;
  padding: 10px;
  background: rgba(220, 53, 69, 0.1);
  border-radius: 4px;
  border-left: 3px solid #dc3545;
}

.modal-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 20px;
}

/* Button Styles */
.btn {
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-primary {
  background-color: #007bff;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background-color: #0056b3;
  box-shadow: 0 2px 8px rgba(0, 123, 255, 0.4);
}

.btn-secondary {
  background-color: #6c757d;
  color: white;
}

.btn-secondary:hover:not(:disabled) {
  background-color: #545b62;
}

.btn-danger {
  background-color: #dc3545;
  color: white;
}

.btn-danger:hover:not(:disabled) {
  background-color: #c82333;
  box-shadow: 0 2px 8px rgba(220, 53, 69, 0.4);
}

.btn-success {
  background-color: #28a745;
  color: white;
}

.btn-success:hover:not(:disabled) {
  background-color: #218838;
  box-shadow: 0 2px 8px rgba(40, 167, 69, 0.4);
}

.btn-sm {
  padding: 6px 12px;
  font-size: 12px;
}

.btn-warning {
  background-color: #ffc107;
  color: #212529;
}

.btn-warning:hover:not(:disabled) {
  background-color: #e0a800;
}

.mr-1 {
  margin-right: 5px;
}

.mr-2 {
  margin-right: 10px;
}

/* Responsive */
@media (max-width: 768px) {
  .filter-section {
    grid-template-columns: 1fr;
  }
  
  .section-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }
  
  .section-header .actions {
    width: 100%;
  }
  
  .permission-table {
    font-size: 12px;
  }
  
  .permission-table th,
  .permission-table td {
    padding: 8px;
  }
}
</style>

